/**
 * AssetManager - Manages owned assets, usage tracking, and cost calculations
 * 
 * Features:
 * - Track quantity of each owned asset (can own multiples)
 * - Track which vehicles are assigned to active missions
 * - Calculate available (not in-use) vehicles
 * - Calculate fuel-only costs for owned vehicles (~1/5 of cheapest vendor)
 * - Handle vehicle loss on mission anomalies
 */

import { 
  OwnableAsset, 
  OWNABLE_VEHICLES, 
  OWNABLE_CREWS,
  getAssetById,
} from '../data/OwnableAssets';
import { Mission, MissionPhase, PHASE_INFO } from './MissionTypes';

/** Fuel cost as fraction of cheapest vendor cost */
export const FUEL_COST_FRACTION = 0.2; // 1/5 of vendor costs

/** Minimum fuel cost floor */
export const MIN_FUEL_COST = 500_000; // $500k minimum

/**
 * Owned asset with quantity tracking
 */
export interface OwnedAssetEntry {
  assetId: string;
  quantity: number;
  /** Missions currently using this asset type */
  assignedMissionIds: string[];
}

/**
 * Vehicle option for mission planning
 */
export interface VehicleOption {
  asset: OwnableAsset;
  isOwned: boolean;
  availableCount: number;  // How many are not in use
  totalOwned: number;
  cost: number;            // Fuel-only if owned, full cost otherwise
  tagline: string;
}

/**
 * Owned crew option for mission planning
 */
export interface CrewOption {
  asset: OwnableAsset;
  isOwned: boolean;
  availableCount: number;
  totalOwned: number;
  cost: number;            // Owned crews have no rental fee
  tagline: string;
}

/**
 * Asset inventory state (for persistence)
 */
export interface AssetInventory {
  /** Map of assetId -> quantity owned */
  owned: Record<string, number>;
  /** Map of assetId -> array of mission IDs using it */
  inUse: Record<string, string[]>;
}

export class AssetManager {
  private inventory: AssetInventory;
  
  constructor(initialInventory?: AssetInventory) {
    this.inventory = initialInventory || {
      owned: {},
      inUse: {},
    };
  }
  
  /**
   * Get current inventory state (for persistence)
   */
  getInventory(): AssetInventory {
    return { ...this.inventory };
  }
  
  /**
   * Load inventory from saved state
   */
  loadInventory(inventory: AssetInventory): void {
    this.inventory = { ...inventory };
  }
  
  // ============================================================================
  // OWNERSHIP MANAGEMENT
  // ============================================================================
  
  /**
   * Add an asset to inventory (purchase)
   */
  purchaseAsset(assetId: string): boolean {
    const asset = getAssetById(assetId);
    if (!asset) {
      console.warn(`[AssetManager] Unknown asset: ${assetId}`);
      return false;
    }
    
    this.inventory.owned[assetId] = (this.inventory.owned[assetId] || 0) + 1;
    return true;
  }
  
  /**
   * Remove an asset from inventory (loss/destruction)
   */
  removeAsset(assetId: string): boolean {
    const currentCount = this.inventory.owned[assetId] || 0;
    if (currentCount <= 0) {
      console.warn(`[AssetManager] Cannot remove asset ${assetId}: none owned`);
      return false;
    }
    
    this.inventory.owned[assetId] = currentCount - 1;
    
    // Clean up if zero
    if (this.inventory.owned[assetId] === 0) {
      delete this.inventory.owned[assetId];
    }
    
    return true;
  }
  
  /**
   * Get quantity owned of an asset
   */
  getOwnedCount(assetId: string): number {
    return this.inventory.owned[assetId] || 0;
  }
  
  /**
   * Check if player owns at least one of an asset
   */
  ownsAsset(assetId: string): boolean {
    return this.getOwnedCount(assetId) > 0;
  }
  
  /**
   * Get all owned assets with quantities
   */
  getAllOwnedAssets(): OwnedAssetEntry[] {
    return Object.entries(this.inventory.owned)
      .filter(([_, qty]) => qty > 0)
      .map(([assetId, quantity]) => ({
        assetId,
        quantity,
        assignedMissionIds: this.inventory.inUse[assetId] || [],
      }));
  }
  
  // ============================================================================
  // USAGE TRACKING
  // ============================================================================
  
  /**
   * Mark an asset as in use by a mission
   */
  assignToMission(assetId: string, missionId: string): boolean {
    if (!this.ownsAsset(assetId)) {
      console.warn(`[AssetManager] Cannot assign ${assetId}: not owned`);
      return false;
    }
    
    const available = this.getAvailableCount(assetId);
    if (available <= 0) {
      console.warn(`[AssetManager] Cannot assign ${assetId}: all in use`);
      return false;
    }
    
    if (!this.inventory.inUse[assetId]) {
      this.inventory.inUse[assetId] = [];
    }
    
    // Don't double-assign
    if (!this.inventory.inUse[assetId].includes(missionId)) {
      this.inventory.inUse[assetId].push(missionId);
    }
    
    return true;
  }
  
  /**
   * Release an asset from a mission (mission complete or cancelled)
   */
  releaseFromMission(assetId: string, missionId: string): void {
    if (!this.inventory.inUse[assetId]) return;
    
    this.inventory.inUse[assetId] = this.inventory.inUse[assetId]
      .filter(id => id !== missionId);
    
    // Clean up empty arrays
    if (this.inventory.inUse[assetId].length === 0) {
      delete this.inventory.inUse[assetId];
    }
  }
  
  /**
   * Get count of available (not in use) assets
   */
  getAvailableCount(assetId: string): number {
    const owned = this.getOwnedCount(assetId);
    const inUse = (this.inventory.inUse[assetId] || []).length;
    return Math.max(0, owned - inUse);
  }
  
  /**
   * Check if an asset is available (at least one not in use)
   */
  isAssetAvailable(assetId: string): boolean {
    return this.getAvailableCount(assetId) > 0;
  }
  
  /**
   * Get the mission ID using a specific instance of an asset
   * Returns first mission using this asset type
   */
  getMissionUsingAsset(assetId: string): string | null {
    const missions = this.inventory.inUse[assetId];
    return missions && missions.length > 0 ? missions[0] : null;
  }
  
  // ============================================================================
  // VEHICLE OPTIONS FOR MISSION PLANNING
  // ============================================================================
  
  /**
   * Calculate fuel-only cost for an owned vehicle
   * ~1/5 of cheapest vendor cost, with a minimum floor
   */
  calculateFuelCost(cheapestVendorCost: number): number {
    const fuelCost = Math.round(cheapestVendorCost * FUEL_COST_FRACTION);
    return Math.max(fuelCost, MIN_FUEL_COST);
  }
  
  /**
   * Get available owned vehicles for mission planning
   * Returns vehicles the player owns that are not currently in use
   */
  getAvailableVehicles(): VehicleOption[] {
    const options: VehicleOption[] = [];
    
    for (const vehicle of OWNABLE_VEHICLES) {
      const ownedCount = this.getOwnedCount(vehicle.id);
      if (ownedCount > 0) {
        const availableCount = this.getAvailableCount(vehicle.id);
        options.push({
          asset: vehicle,
          isOwned: true,
          availableCount,
          totalOwned: ownedCount,
          cost: 0, // Will be calculated with vendor context
          tagline: 'Use your own rig — pay only for fuel',
        });
      }
    }
    
    return options;
  }
  
  /**
   * Get vehicle options for mission planning, including owned vehicles at top
   * @param vendorCosts Array of vendor costs for fuel calculation
   */
  getVehicleOptionsForMission(vendorCosts: number[]): VehicleOption[] {
    const cheapestVendorCost = vendorCosts.length > 0 
      ? Math.min(...vendorCosts) 
      : 10_000_000;
    
    const fuelCost = this.calculateFuelCost(cheapestVendorCost);
    
    const options: VehicleOption[] = [];
    
    // Add owned vehicles first
    for (const vehicle of OWNABLE_VEHICLES) {
      const ownedCount = this.getOwnedCount(vehicle.id);
      if (ownedCount > 0) {
        const availableCount = this.getAvailableCount(vehicle.id);
        options.push({
          asset: vehicle,
          isOwned: true,
          availableCount,
          totalOwned: ownedCount,
          cost: availableCount > 0 ? fuelCost : 0, // Only show cost if available
          tagline: availableCount > 0 
            ? 'Use your own rig — pay only for fuel'
            : `All ${ownedCount} in use on active missions`,
        });
      }
    }
    
    return options;
  }

  // ============================================================================
  // CREW OPTIONS FOR MISSION PLANNING
  // ============================================================================

  /**
   * Get owned crew options for mission planning.
   */
  getCrewOptionsForMission(): CrewOption[] {
    const options: CrewOption[] = [];

    for (const crew of OWNABLE_CREWS) {
      const ownedCount = this.getOwnedCount(crew.id);
      if (ownedCount > 0) {
        const availableCount = this.getAvailableCount(crew.id);
        options.push({
          asset: crew,
          isOwned: true,
          availableCount,
          totalOwned: ownedCount,
          cost: 0,
          tagline: availableCount > 0
            ? 'Use your own crew — no rental fees'
            : `All ${ownedCount} in use on active missions`,
        });
      }
    }

    return options;
  }
  
  // ============================================================================
  // MISSION ANOMALY HANDLING
  // ============================================================================
  
  /**
   * Phases that result in vehicle destruction
   */
  private static readonly VEHICLE_LOSS_PHASES: MissionPhase[] = [
    MissionPhase.LAUNCH_ANOMALY,
    MissionPhase.IN_FLIGHT_ANOMALY,
    MissionPhase.EXPLOSION_AT_DRILL_SITE,
  ];
  
  /**
   * Check if a mission phase results in vehicle loss
   */
  isVehicleLossPhase(phase: MissionPhase): boolean {
    return AssetManager.VEHICLE_LOSS_PHASES.includes(phase);
  }
  
  /**
   * Handle vehicle loss from mission anomaly
   * Call this when mission enters an anomaly phase
   * Returns the lost vehicle info, or null if no owned vehicle was used
   */
  handleMissionAnomaly(mission: Mission): OwnableAsset | null {
    const vehicleId = mission.ownedVehicleId;
    if (mission.ownedCrewId) {
      this.releaseFromMission(mission.ownedCrewId, mission.id);
    }

    if (!vehicleId) {
      return null; // Mission didn't use an owned vehicle
    }
    
    const vehicle = getAssetById(vehicleId);
    if (!vehicle) {
      console.warn(`[AssetManager] Unknown vehicle in mission: ${vehicleId}`);
      return null;
    }
    
    // Release from mission tracking
    this.releaseFromMission(vehicleId, mission.id);
    
    // Remove from inventory (vehicle destroyed)
    this.removeAsset(vehicleId);
    
    console.log(`[AssetManager] Vehicle destroyed: ${vehicle.name} (mission: ${mission.id})`);
    
    return vehicle;
  }
  
  /**
   * Handle mission completion (success or failure without vehicle loss)
   * Releases the vehicle back to available pool
   */
  handleMissionComplete(mission: Mission): void {
    const vehicleId = mission.ownedVehicleId;
    if (vehicleId) {
      this.releaseFromMission(vehicleId, mission.id);
      console.log(`[AssetManager] Vehicle returned: ${vehicleId} (mission: ${mission.id})`);
    }

    const crewId = mission.ownedCrewId;
    if (crewId) {
      this.releaseFromMission(crewId, mission.id);
      console.log(`[AssetManager] Crew returned: ${crewId} (mission: ${mission.id})`);
    }
  }
  
  // ============================================================================
  // MIGRATION: Convert from string[] to inventory
  // ============================================================================
  
  /**
   * Migrate from old ownedAssets: string[] format to new inventory format
   * Each asset ID in the old format becomes quantity: 1
   */
  static migrateFromStringArray(oldAssets: string[]): AssetInventory {
    const owned: Record<string, number> = {};
    
    for (const assetId of oldAssets) {
      owned[assetId] = (owned[assetId] || 0) + 1;
    }
    
    return {
      owned,
      inUse: {},
    };
  }
  
  /**
   * Sync in-use tracking with active missions
   * Call this on game load to ensure consistency
   */
  syncWithActiveMissions(missions: Mission[]): void {
    // Clear current in-use tracking
    this.inventory.inUse = {};
    
    // Rebuild from active missions
    for (const mission of missions) {
      if (PHASE_INFO[mission.currentPhase]?.isTerminal) {
        continue;
      }

      if (mission.ownedVehicleId && !this.isVehicleLossPhase(mission.currentPhase)) {
        this.assignToMission(mission.ownedVehicleId, mission.id);
      }
      if (mission.ownedCrewId) {
        this.assignToMission(mission.ownedCrewId, mission.id);
      }
    }
  }
  
  /**
   * Sync inventory from gameState's ownedAssets array
   * Used at startup to bridge legacy array-based ownership to quantity tracking
   */
  syncFromLegacyAssets(ownedAssetIds: string[]): void {
    // Count occurrences of each asset ID
    const counts: Record<string, number> = {};
    for (const id of ownedAssetIds) {
      counts[id] = (counts[id] || 0) + 1;
    }
    
    // Update inventory owned counts
    this.inventory.owned = counts;
    
    console.log('[AssetManager] Synced from legacy assets:', counts);
  }
}

// Singleton instance
let assetManagerInstance: AssetManager | null = null;

export function getAssetManager(): AssetManager {
  if (!assetManagerInstance) {
    assetManagerInstance = new AssetManager();
  }
  return assetManagerInstance;
}

export function initAssetManager(inventory?: AssetInventory): AssetManager {
  assetManagerInstance = new AssetManager(inventory);
  return assetManagerInstance;
}
